//eslint-disable-next-line
//@ts-nocheck

import { LineStudyPlotStyle } from "@/lib/datafeed/utils.ts";
import {
  CustomIndicator,
  IPineStudyResult,
  LibraryPineStudy,
  PineJS,
  RawStudyMetaInfoId,
} from "@tradingView/charting_library";

import {
  StudyInputType,
  StudyPlotType,
  StudyTargetPriceScale,
} from "@/lib/datafeed/tvTypes.ts";

import {
  ParseSymbol,
  AccessCheck,
} from "@/lib/indicators/indicators-common-functions";

import { IIndicatorOptions } from "@/domain/interfaces/general.interface";

export const tradingSessionsHighlighter = (
  PineJS: PineJS,
  options?: IIndicatorOptions,
): CustomIndicator => {
  return {
    name: "Trading sessions highlighter",
    metainfo: {
      _metainfoVersion: 51,
      id: "trading-sessions-highlighter@tv-basicstudies-1" as RawStudyMetaInfoId,
      description: "[SFM] Trading sessions highlighter",
      shortDescription: "[SF] Sessions",
      is_hidden_study: false,
      is_price_study: true,
      isCustomIndicator: true,
      linkedToSeries: true,
      format: {
        type: "price",
        precision: 2,
      },
      plots: [
        {
          id: "sessionAPlot",
          type: StudyPlotType.BgColorer,
          palette: "sessionAPalette",
        },
        {
          id: "sessionBPlot",
          type: StudyPlotType.BgColorer,
          palette: "sessionBPalette",
        },
        {
          id: "sessionCPlot",
          type: StudyPlotType.BgColorer,
          palette: "sessionCPalette",
        },
        {
          id: "sessionDPlot",
          type: StudyPlotType.BgColorer,
          palette: "sessionDPalette",
        },
      ],
      inputs: [
        {
          id: "sessionANameInput",
          name: "",
          defval: "Tokyo",
          inline: true,
          type: StudyInputType.Text,
          group: "Session A",
        },
        {
          id: "sessionATimeInput",
          name: "",
          defval: "0000-0900",
          inline: true,
          type: StudyInputType.Session,
          group: "Session A",
        },
        {
          id: "sessionBNameInput",
          name: "",
          defval: "Frankfurt",
          inline: true,
          type: StudyInputType.Text,
          group: "Session B",
        },
        {
          id: "sessionBTimeInput",
          name: "",
          defval: "0700-1500",
          inline: true,
          type: StudyInputType.Session,
          group: "Session B",
        },
        {
          id: "sessionCNameInput",
          name: "",
          defval: "London",
          inline: true,
          type: StudyInputType.Text,
          group: "Session C",
        },
        {
          id: "sessionCTimeInput",
          name: "",
          defval: "0800-1600",
          inline: true,
          type: StudyInputType.Session,
          group: "Session C",
        },
        {
          id: "sessionDNameInput",
          name: "",
          defval: "New York",
          inline: true,
          type: StudyInputType.Text,
          group: "Session D",
        },
        {
          id: "sessionDTimeInput",
          name: "",
          defval: "1300-2100",
          inline: true,
          type: StudyInputType.Session,
          group: "Session D",
        },
      ],
      palettes: {
        sessionAPalette: {
          colors: [{ name: "Session A color" }],
          valToIndex: {
            0: 0,
          },
        },
        sessionBPalette: {
          colors: [{ name: "Session B color" }],
          valToIndex: {
            0: 0,
          },
        },
        sessionCPalette: {
          colors: [{ name: "Session C color" }],
          valToIndex: {
            0: 0,
          },
        },
        sessionDPalette: {
          colors: [{ name: "Session D color" }],
          valToIndex: {
            0: 0,
          },
        },
      },
      defaults: {
        inputs: {
          sessionANameInput: "Tokyo",
          sessionATimeInput: "0000-0900",
          sessionBNameInput: "Frankfurt",
          sessionBTimeInput: "0700-1500",
          sessionCNameInput: "London",
          sessionCTimeInput: "0800-1600",
          sessionDNameInput: "New York",
          sessionDTimeInput: "1300-2100",
        },
        palettes: {
          sessionAPalette: {
            colors: [{ color: "rgba(178, 24, 44, 0.10)" }],
          },
          sessionBPalette: {
            colors: [{ color: "rgba(255, 160, 40, 0.10)" }],
          },
          sessionCPalette: {
            colors: [{ color: "rgba(60, 166, 75, 0.10)" }],
          },
          sessionDPalette: {
            colors: [{ color: "rgba(13, 157, 219, 0.10)" }],
          },
        },
        styles: {},
      },
      styles: {
        sessionAPlot: {
          title: "Session A",
          histogramBase: 0,
        },
        sessionBPlot: {
          title: "Session B",
          histogramBase: 0,
        },
        sessionCPlot: {
          title: "Session C",
          histogramBase: 0,
        },
        sessionDPlot: {
          title: "Session D",
          histogramBase: 0,
        },
      },
    },

    constructor: function () {
      this.init = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;
      };

      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        // Get inputs
        const sessionATimeInput: string = this._input(1);
        const sessionBTimeInput: string = this._input(3);
        const sessionCTimeInput: string = this._input(5);
        const sessionDTimeInput: string = this._input(7);

        // Functions
        const sessionCheck = (time: number, sessionTime: string): number => {
          const convertTimeToMinutes = (timeStr: string): number => {
            const hours: number = Math.floor(Number(timeStr) / 100);
            const minutes: number = Number(timeStr) % 100;
            return hours * 60 + minutes;
          };

          const [startTimeStr, endTimeStr]: string[] = sessionTime.split("-");
          const sessionStart: number = convertTimeToMinutes(startTimeStr);
          const sessionEnd: number = convertTimeToMinutes(endTimeStr);

          const session: number =
            time >= sessionStart && time <= sessionEnd ? 0 : NaN;

          return session;
        };

        // Market data parsing
        const currentTime: number =
          PineJS.Std.minute(this._context) +
          PineJS.Std.hour(this._context) * 60;

        // Colorer
        const sessionA: number = sessionCheck(currentTime, sessionATimeInput);
        const sessionB: number = sessionCheck(currentTime, sessionBTimeInput);
        const sessionC: number = sessionCheck(currentTime, sessionCTimeInput);
        const sessionD: number = sessionCheck(currentTime, sessionDTimeInput);

        // Return
        return [sessionA, sessionB, sessionC, sessionD];
      };
    },
  };
};
